import type { ChatTimelineEntry } from '@server/chat/chat.dto';
import type { PaginatedResult } from '@server/common/paginated.dto';
import type { InfiniteData } from '@tanstack/react-query';

type InfinitePaginatedQueryData<TItem> = InfiniteData<
  PaginatedResult<TItem>,
  number
>;

const incrementOptional = (value: number | undefined): number | undefined =>
  value === undefined ? undefined : value + 1;

/**
 * The prepended entry shifts every later entry one step further from the
 * start, so `nextOffset` moves along with `total`. Bumping only `total` would
 * make the next page start one entry early and fetch a row that is already
 * cached.
 */
const shiftPaginationByOne = <T>(
  page: PaginatedResult<T>,
): PaginatedResult<T> => {
  const { total, nextOffset } = page.meta.pagination;

  return {
    ...page,
    meta: {
      ...page.meta,
      pagination: {
        ...page.meta.pagination,
        total: total + 1,
        nextOffset: incrementOptional(nextOffset),
      },
    },
  };
};

export const prependChatMessageToInfinitePaginatedData = (
  previous: InfinitePaginatedQueryData<ChatTimelineEntry> | undefined,
  message: ChatTimelineEntry,
): InfinitePaginatedQueryData<ChatTimelineEntry> => {
  if (!previous) {
    return {
      pageParams: [0],
      pages: [
        {
          meta: { pagination: { total: 1 } },
          data: [message],
        },
      ],
    };
  }

  const [firstPage, ...restPages] = previous.pages;
  if (!firstPage) {
    return {
      ...previous,
      pages: [
        {
          meta: { pagination: { total: 1 } },
          data: [message],
        },
      ],
    };
  }

  return {
    ...previous,
    pages: [
      {
        ...shiftPaginationByOne(firstPage),
        data: [message, ...firstPage.data],
      },
      ...restPages.map(shiftPaginationByOne),
    ],
  };
};
